// StructuralAncestryTrail.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { normalizeAncestryPath } from 'utilities/nodeAncestry';

// Services
import { getNodeBreadcrumbFields } from 'services/nodes/NodeReadService';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// CSS
import './StructuralAncestryTrail.css';

// Icons
import Icon from 'svg/Icon';

/**
 * StructuralAncestryTrail
 *
 * Renders a clickable structural trail from node.ancestryPath
 * (e.g. Project > Purchase Order), separate from visit-history crumbs.
 *
 * @param {object} props
 * @param {object|null|undefined} props.node - Current node (reads ancestryPath).
 * @param {'default'|'compact'} [props.variant='default'] - compact = single-line ellipsis (Peek).
 * @param {boolean} [props.preferPeek=false] - Open crumb targets in Peek when possible.
 * @returns {ReactNode}
 */
const StructuralAncestryTrail = React.memo(({
  node,
  variant = 'default',
  preferPeek = false,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- HOOKS ----- //

  const { openNode } = useNodeOpenIntent();

  // ----- MEMOS ----- //

  const ancestryPath = useMemo(
    () => normalizeAncestryPath(node?.ancestryPath),
    [node?.ancestryPath],
  );

  const ancestryKeys = useMemo(
    () => ancestryPath.map((entry) => entry.key),
    [ancestryPath],
  );

  const isCompact = variant === 'compact';

  // ----- STATE ----- //

  // Hydrated titles for ancestry keys.
  const [titleByKey, setTitleByKey] = useState(() => new Map());

  // Hovered crumb key for label color.
  const [hoveredKey, setHoveredKey] = useState('');

  // ----- EFFECTS ----- //

  // Batch-load titles for ancestry path keys.
  useEffect(() => {
    let cancelled = false;
    if (!ancestryKeys.length) {
      setTitleByKey(new Map());
      return undefined;
    }

    getNodeBreadcrumbFields(ancestryKeys)
      .then((map) => {
        if (!cancelled) setTitleByKey(map || new Map());
      })
      .catch(() => {
        if (!cancelled) setTitleByKey(new Map());
      });

    return () => {
      cancelled = true;
    };
  }, [ancestryKeys]);

  // ----- HANDLERS ----- //

  /**
   * Open an ancestry node.
   *
   * @param {string} nodeKey
   * @param {string} typeKey
   * @returns {void}
   */
  const handleCrumbClick = (nodeKey, typeKey) => {
    const key = String(nodeKey || '').trim();
    if (!key) return;
    openNode(
      {
        key,
        type: String(typeKey || '').trim() || undefined,
        title: titleByKey.get(key)?.title || undefined,
      },
      { preferPeek: preferPeek === true },
    );
  };

  // ----- RENDER ----- //

  if (!ancestryPath.length) return null;

  return (
    <div
      className={[
        'structural-ancestry-trail-container',
        isCompact ? 'structural-ancestry-trail-compact' : '',
      ].filter(Boolean).join(' ')}
    >
      {ancestryPath.map((entry, index) => {
        const isLast = index === ancestryPath.length - 1;
        const fields = titleByKey.get(entry.key);
        const label = String(fields?.title || '').trim();
        const isHovered = hoveredKey === entry.key;

        return (
          <span
            className="structural-ancestry-trail-item"
            key={`${entry.type}:${entry.key}`}
          >

            {/* LINK */}
            <span
              className="structural-ancestry-trail-link"
              title={label || undefined}
              onClick={() => handleCrumbClick(entry.key, entry.type)}
              onMouseEnter={() => setHoveredKey(entry.key)}
              onMouseLeave={() => setHoveredKey('')}
            >
              <span
                className="structural-ancestry-trail-label"
                style={{
                  color: isHovered ? theme.foreground : theme.foregroundMuted,
                }}
              >
                {label}
              </span>
            </span>

            {/* SEPARATOR */}
            {!isLast ? (
              <span className="structural-ancestry-trail-separator">
                <Icon
                  iconKey="chevron_right"
                  width={14}
                  height={14}
                  color={theme.foregroundMuted}
                />
              </span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
});

// Dev Tools
StructuralAncestryTrail.displayName = 'StructuralAncestryTrail';

export default StructuralAncestryTrail;
